import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Target, ChevronDown, ChevronUp, Trash2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STATUS_STYLES = {
  on_track:  { color: "#22c55e", bg: "rgba(34,197,94,0.12)" },
  at_risk:   { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  behind:    { color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  completed: { color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
  cancelled: { color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
};

export default function PerformanceOKRs() {
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [okrs, setOkrs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedOkr, setExpandedOkr] = useState(null);

  const [form, setForm] = useState({
    cycle_id: "",
    employee_id: "",
    title: "",
    description: "",
    due_date: "",
    key_results: [],
  });

  useEffect(() => {
    api.get("v1/performance/cycles/")
      .then((r) => {
        const list = r.data || [];
        setCycles(list);
        const active = list.find((c) => c.status === "active");
        if (active) setSelectedCycleId(String(active.id));
      })
      .catch(() => {});
    api.get("v1/employees/")
      .then((r) => setEmployees(r.data?.employees || r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCycleId) return;
    setLoading(true);
    api.get(`v1/performance/cycles/${selectedCycleId}/okrs/`)
      .then((r) => setOkrs(r.data || []))
      .catch(() => toast.error(t("performance.okrs.toasts.loadError")))
      .finally(() => setLoading(false));
  }, [selectedCycleId]);

  const addKeyResult = () => {
    const id = `kr${Date.now()}`;
    setForm((f) => ({
      ...f,
      key_results: [
        ...f.key_results,
        { id, title: "", target_value: 100, current_value: 0, unit: "%" },
      ],
    }));
  };

  const updateKR = (id, field, value) => {
    setForm((f) => ({
      ...f,
      key_results: f.key_results.map((kr) =>
        kr.id === id ? { ...kr, [field]: value } : kr
      ),
    }));
  };

  const removeKR = (id) => {
    setForm((f) => ({ ...f, key_results: f.key_results.filter((kr) => kr.id !== id) }));
  };

  const handleCreate = async () => {
    if (!form.cycle_id || !form.employee_id || !form.title) {
      toast.error(t("performance.okrs.requiredFields"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`v1/performance/cycles/${form.cycle_id}/okrs/`, {
        ...form,
        cycle_id: Number(form.cycle_id),
        employee_id: Number(form.employee_id),
      });
      toast.success(t("performance.okrs.toasts.created"));
      setShowForm(false);
      setForm({ cycle_id: selectedCycleId, employee_id: "", title: "", description: "", due_date: "", key_results: [] });
      if (selectedCycleId) {
        api.get(`v1/performance/cycles/${selectedCycleId}/okrs/`)
          .then((r) => setOkrs(r.data || []));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("performance.okrs.toasts.createError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("performance.okrs.confirmDelete"))) return;
    try {
      await api.delete(`v1/performance/okrs/${id}/`);
      toast.success(t("performance.okrs.toasts.deleted"));
      setOkrs((prev) => prev.filter((o) => o.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold" style={{ color: "var(--lm-ink)" }}>{t("performance.okrs.title")}</h3>
        <div className="flex items-center gap-2">
          <select
            className="rounded-md border px-3 py-1.5 text-sm"
            style={{ backgroundColor: "var(--lm-canvas)", borderColor: "var(--lm-border)", color: "var(--lm-ink)" }}
            value={selectedCycleId}
            onChange={(e) => setSelectedCycleId(e.target.value)}
          >
            <option value="">{t("performance.okrs.selectCycle")}</option>
            {cycles.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
          <Button size="sm" onClick={() => { setForm((f) => ({ ...f, cycle_id: selectedCycleId })); setShowForm(!showForm); }}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t("performance.okrs.new")}
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #0ea5e9" }}>
          <h4 className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>{t("performance.okrs.createTitle")}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.okrs.fieldCycle")}</label>
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ backgroundColor: "var(--lm-canvas)", borderColor: "var(--lm-border)", color: "var(--lm-ink)" }}
                value={form.cycle_id}
                onChange={(e) => setForm({ ...form, cycle_id: e.target.value })}
              >
                <option value="">{t("performance.okrs.choose")}</option>
                {cycles.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.okrs.fieldEmployee")}</label>
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ backgroundColor: "var(--lm-canvas)", borderColor: "var(--lm-border)", color: "var(--lm-ink)" }}
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              >
                <option value="">{t("performance.okrs.choose")}</option>
                {employees.map((e) => (
                  <option key={e.id} value={String(e.id)}>
                    {e.first_name} {e.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.okrs.fieldTitle")}</label>
              <Input
                placeholder={t("performance.okrs.titlePlaceholder")}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.okrs.fieldDescription")}</label>
              <Input
                placeholder={t("performance.okrs.descPlaceholder")}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.okrs.fieldDueDate")}</label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>

          {/* Key Results */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold" style={{ color: "var(--lm-ink-2)" }}>
                {t("performance.okrs.keyResults", { count: form.key_results.length })}
              </label>
              <button
                onClick={addKeyResult}
                className="text-xs flex items-center gap-1 px-2 py-1 rounded-md hover:opacity-80"
                style={{ color: "#0ea5e9", backgroundColor: "rgba(14,165,233,0.08)" }}
              >
                <Plus className="h-3 w-3" /> {t("performance.okrs.addKr")}
              </button>
            </div>
            {form.key_results.map((kr) => (
              <div key={kr.id} className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: "var(--lm-canvas)", border: "1px solid var(--lm-border)" }}>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <Input
                    placeholder={t("performance.okrs.krTitlePlaceholder")}
                    className="sm:col-span-2"
                    value={kr.title}
                    onChange={(e) => updateKR(kr.id, "title", e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder={t("performance.okrs.krTarget")}
                    value={kr.target_value}
                    onChange={(e) => updateKR(kr.id, "target_value", Number(e.target.value))}
                  />
                  <Input
                    placeholder={t("performance.okrs.krUnit")}
                    value={kr.unit}
                    onChange={(e) => updateKR(kr.id, "unit", e.target.value)}
                  />
                </div>
                <button onClick={() => removeKR(kr.id)} className="p-1 rounded" style={{ color: "#ef4444" }}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
            <Button size="sm" onClick={handleCreate} disabled={submitting}>
              {submitting ? t("performance.okrs.creating") : t("performance.okrs.createCta")}
            </Button>
          </div>
        </div>
      )}

      {!selectedCycleId ? (
        <div className="rounded-xl p-8 text-center" style={{ backgroundColor: "var(--lm-s2)", border: "1px dashed var(--lm-border)" }}>
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{t("performance.okrs.selectCyclePrompt")}</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-6 w-6 rounded-full border-b-2" style={{ borderColor: "#0ea5e9" }} />
        </div>
      ) : okrs.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ backgroundColor: "var(--lm-s2)", border: "1px dashed var(--lm-border)" }}>
          <Target className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--lm-ink-3)" }} />
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t("performance.okrs.emptyPre")}<strong>{t("performance.okrs.new")}</strong>{t("performance.okrs.emptyPost")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {okrs.map((okr) => (
            <OKRCard
              key={okr.id}
              okr={okr}
              t={t}
              expanded={expandedOkr === okr.id}
              onToggle={() => setExpandedOkr(expandedOkr === okr.id ? null : okr.id)}
              onDelete={() => handleDelete(okr.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OKRCard({ okr, t, expanded, onToggle, onDelete }) {
  const sty = STATUS_STYLES[okr.status] || STATUS_STYLES.on_track;
  const statusLabel = t(`performance.okrs.status.${okr.status}`, { defaultValue: t("performance.okrs.status.on_track") });
  const krs = okr.key_results || [];
  const progress = okr.progress_pct ?? 0;

  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}>
      <div className="flex items-center justify-between px-4 py-3.5">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Target className="h-4 w-4 flex-shrink-0" style={{ color: "#8b5cf6" }} />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: "var(--lm-ink)" }}>{okr.title}</p>
            <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
              {okr.employee_name || t("performance.okrs.employeeFallback", { id: okr.employee_id })} · {krs.length} KR{krs.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-3">
          <div className="hidden sm:flex items-center gap-1.5 text-xs" style={{ color: "var(--lm-ink-2)" }}>
            <span className="font-medium" style={{ color: "var(--lm-ink)" }}>{Math.round(progress)}%</span>
          </div>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: sty.bg, color: sty.color }}>
            {statusLabel}
          </span>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-md hover:opacity-80 transition-opacity"
            title={t("common.delete")}
            style={{ backgroundColor: "rgba(239,68,68,0.12)", color: "#ef4444" }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <button onClick={onToggle} className="p-1.5 rounded-md" style={{ color: "var(--lm-ink-3)" }}>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-2">
        <div className="h-1.5 rounded-full" style={{ backgroundColor: "var(--lm-border)" }}>
          <div
            className="h-1.5 rounded-full transition-all"
            style={{ width: `${Math.min(progress, 100)}%`, backgroundColor: progress >= 70 ? "#22c55e" : progress >= 40 ? "#f59e0b" : "#ef4444" }}
          />
        </div>
      </div>

      {expanded && krs.length > 0 && (
        <div className="px-4 pb-4 space-y-2" style={{ borderTop: "1px solid var(--lm-border)" }}>
          <p className="text-xs font-semibold mt-3" style={{ color: "var(--lm-ink-2)" }}>{t("performance.okrs.krsLabel")}</p>
          {krs.map((kr) => {
            const pct = kr.target_value > 0 ? Math.min((kr.current_value / kr.target_value) * 100, 100) : 0;
            return (
              <div key={kr.id} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5" style={{ color: "var(--lm-ink)" }}>
                    {pct >= 100 ? (
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#22c55e" }} />
                    ) : (
                      <Circle className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--lm-ink-3)" }} />
                    )}
                    {kr.title || t("performance.okrs.krUntitled")}
                  </div>
                  <span style={{ color: "var(--lm-ink-2)" }}>
                    {kr.current_value}/{kr.target_value} {kr.unit}
                  </span>
                </div>
                <div className="h-1 rounded-full ml-5" style={{ backgroundColor: "var(--lm-border)" }}>
                  <div
                    className="h-1 rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "#22c55e" : "#0ea5e9" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
